import React, { useEffect, useMemo, useRef, useState } from "react";
import ToastUI, {
  ToastContainer as ToastStackContainer,
} from "../Modules/Assignments/Components/ToastUI.jsx";

const subscribers = new Set();
const queuedEvents = [];

const normalizeEvent = (type, message, options = {}) => {
  const titleFromType =
    type === "success"
      ? "Success"
      : type === "error"
      ? "Error"
      : type === "warning"
      ? "Warning"
      : "Info";

  return {
    id:
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random()}`,
    type:
      type === "warn" || type === "warning" || type === "info"
        ? "warning"
        : type,
    title: options?.title || titleFromType,
    message:
      typeof message === "string" ? message : JSON.stringify(message ?? ""),
    duration: typeof options?.autoClose === "number" ? options.autoClose : null,
  };
};

const emitToast = (type, message, options) => {
  const event = normalizeEvent(type, message, options);
  if (subscribers.size === 0) {
    queuedEvents.push(event);
    return;
  }
  subscribers.forEach((sub) => sub(event));
};

export const toast = {
  success: (message, options) => emitToast("success", message, options),
  error: (message, options) => emitToast("error", message, options),
  warn: (message, options) => emitToast("warning", message, options),
  warning: (message, options) => emitToast("warning", message, options),
  info: (message, options) => emitToast("warning", message, options),
};

export const ToastContainer = ({
  position = "top-right",
  autoClose = 5000,
  style,
}) => {
  const [toasts, setToasts] = useState([]);
  const mountedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    const handler = (event) => {
      setToasts((prev) => [...prev, event]);
    };
    subscribers.add(handler);

    if (queuedEvents.length > 0) {
      const backlog = [...queuedEvents];
      queuedEvents.length = 0;
      setToasts((prev) => [...prev, ...backlog]);
    }

    return () => {
      mountedRef.current = false;
      subscribers.delete(handler);
    };
  }, []);

  const removeToast = (id) => {
    if (!mountedRef.current) return;
    setToasts((prev) => prev.filter((item) => item.id !== id));
  };

  const renderedToasts = useMemo(() => toasts, [toasts]);

  return (
    <div style={style}>
      <ToastStackContainer position={position}>
        {renderedToasts.map((item) => (
          <ToastUI
            key={item.id}
            type={item.type}
            title={item.title}
            message={item.message}
            duration={item.duration ?? autoClose}
            onClose={() => removeToast(item.id)}
          />
        ))}
      </ToastStackContainer>
    </div>
  );
};

